import { Link } from 'react-router-dom';

import { useUserProfile } from '@/hooks/useUserProfile';
import { Badge, Button, Card, CardTitle } from '@/components/ui';
import { CategoriaBadge } from '@/components/CategoriaBadge';
import { formatEventDate } from '@/lib/format';

/**
 * AccountDashboard — `/account` index. Avatar + categoría de fidelidad +
 * quick links a sub-secciones. Perfil real desde `public.user_profiles`
 * (ROUND8 UAT-C05) vía `useUserProfile()`. ROUND14: el badge de categoría
 * sale del componente `CategoriaBadge`.
 */
export function AccountDashboard(): JSX.Element {
  const { data: profile, isLoading, isError } = useUserProfile();

  if (isLoading) {
    return (
      <Card variant="default" padded aria-busy="true">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 flex-none rounded-full bg-neutral-100" />
          <div className="flex-1">
            <div className="h-6 w-40 rounded bg-neutral-100" />
            <div className="mt-2 h-4 w-56 rounded bg-neutral-100" />
          </div>
        </div>
      </Card>
    );
  }

  if (isError || !profile) {
    return (
      <Card variant="outlined" padded>
        <CardTitle>No pudimos cargar tu cuenta</CardTitle>
        <p className="mt-2 text-sm text-fc-muted" role="alert">
          Ocurrió un error al cargar tu perfil. Probá recargar la página en unos minutos.
        </p>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card variant="default" padded>
        <div className="flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            <div
              aria-hidden
              className="flex h-16 w-16 flex-none items-center justify-center rounded-full bg-primary text-xl font-semibold text-fc-bg"
            >
              {profile.initials}
            </div>
            <div className="min-w-0">
              <CardTitle>{profile.fullName}</CardTitle>
              <p className="text-sm text-fc-muted">{profile.email}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-fc-muted">
                <CategoriaBadge value={profile.loyaltyTier} />
                {profile.memberSince && (
                  <span>Miembro desde {formatEventDate(profile.memberSince)}</span>
                )}
              </div>
            </div>
          </div>
          <Button as={Link} to="/account/profile" variant="secondary" size="sm">
            Editar perfil
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <QuickLinkCard
          to="/account/membership"
          title="Mi membresía"
          summary={
            profile.subscriptionActive && profile.subscriptionPlan
              ? `Plan ${profile.subscriptionPlan} activo.`
              : 'Sin suscripción activa — explorá los planes.'
          }
          ctaLabel="Ver detalle"
          tone="gold"
        />
        <QuickLinkCard
          to="/account/payment-methods"
          title="Métodos de pago"
          summary="Tarjetas guardadas para checkout 1-clic."
          ctaLabel="Administrar"
          tone="info"
        />
        <QuickLinkCard
          to="/account/notifications"
          title="Notificaciones"
          summary="Email, SMS, push y marketing."
          ctaLabel="Configurar"
          tone="neutral"
        />
        <QuickLinkCard
          to="/my-purchases"
          title="Mis compras"
          summary="Historial unificado: boletas, productos, membresías y experiencias."
          ctaLabel="Ver compras"
          tone="success"
        />
      </div>

      <Card variant="ghost" padded>
        <CardTitle>Puntos First Class</CardTitle>
        <p className="mt-2 text-sm text-fc-muted">
          Tenés{' '}
          <span className="font-semibold tabular-nums text-fc-ink">
            {profile.points.toLocaleString('es-CO')}
          </span>{' '}
          puntos disponibles
          {profile.lifetimePoints > 0 && (
            <> · {profile.lifetimePoints.toLocaleString('es-CO')} acumulados de por vida</>
          )}
          .
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button as={Link} to="/account/membership" variant="primary" size="sm">
            Mi membresía
          </Button>
          <Button as={Link} to="/memberships" variant="ghost" size="sm">
            Comparar planes
          </Button>
        </div>
      </Card>
    </div>
  );
}

function QuickLinkCard({
  to,
  title,
  summary,
  ctaLabel,
  tone,
}: {
  to: string;
  title: string;
  summary: string;
  ctaLabel: string;
  tone: 'gold' | 'info' | 'neutral' | 'success';
}): JSX.Element {
  return (
    <Card variant="default" padded>
      <Badge tone={tone}>{title}</Badge>
      <p className="mt-3 text-sm text-fc-muted">{summary}</p>
      <Button as={Link} to={to} variant="secondary" size="sm" className="mt-4">
        {ctaLabel}
      </Button>
    </Card>
  );
}
